import React, { useEffect, useState } from 'react';
import { tmRequest } from '../../utils/tmApi';
import { isTm, isTmFinal } from '../../utils/tmPricing';

export const TM_FINAL_REFUSAL =
  'Priced T&M order: new jobs are refused. Reopen T&M pricing on the Sales Order first (before any invoice).';

/**
 * What Job Entry allows on the selected order. A T&M order's own lines take no surplus allocation
 * (allowAllocate: false); a shared sheet's OTHER parts (the orphan "Other Parts Being Produced" rows)
 * are still allocated to their orders (since 2026-10-05). A PRICED T&M order takes no new jobs in
 * Sales Order mode (the API refuses them too).
 * A fixed-price order (or no order) allows both, exactly as before T&M.
 * Returns {allowAllocate, createBlockedReason}.
 */
export function tmJobEntryGate(entryMode, header) {
  return {
    allowAllocate: !isTm(header),
    createBlockedReason: entryMode === 'sales_order' && isTmFinal(header) ? TM_FINAL_REFUSAL : null,
  };
}

/**
 * Job Entry in Sales Order mode on a Time & Materials order: how to load it, the overrun hints
 * (GET tm_load_warnings — the customer pays for every good piece), and the refusal on a priced
 * order. Renders nothing for a fixed-price order. The "is this a remake of defect #X?" hints
 * modal comes with the defect capture work.
 */
export default function TmJobEntryPanel({ header }) {
  const [overrun, setOverrun] = useState([]);
  const soId = header && header.sales_order_id;
  const tm = isTm(header);
  const final = isTmFinal(header);

  useEffect(() => {
    setOverrun([]);
    if (!tm || final || !soId) return undefined;
    let alive = true;
    tmRequest('GET', `/sales_orders/${soId}/tm_load_warnings`).then((r) => {
      if (alive && r.ok && r.data && Array.isArray(r.data.overrun)) setOverrun(r.data.overrun);
    });
    return () => { alive = false; };
  }, [tm, final, soId]);

  if (!tm) return null;
  if (final) {
    return (
      <div className="alert alert-danger py-2 small mt-2" data-testid="tm-job-entry-final">
        <i className="bi bi-lock-fill me-1"></i>{TM_FINAL_REFUSAL}
      </div>
    );
  }
  return (
    <div className="alert alert-info py-2 small mt-2" data-testid="tm-job-entry-panel">
      <strong><i className="bi bi-calculator me-1"></i>Time &amp; Materials order</strong>
      <ul className="mb-0 ps-3">
        <li>Load jobs on this order: the customer pays for every good piece of its lines.</li>
        <li>A shared sheet may also carry other orders' parts. Allocate all of them to their orders (Other Parts Being Produced → Allocate) before pricing; each order pays only for its own parts' share of the sheet.</li>
        <li>Bad Idea Co. filler parts (KC…) may ride along with no order: they auto-ship to Bad Idea stock and its weekly invoice bills them. They need no allocation and don't hold up pricing.</li>
        <li>Surplus of this order's own lines can't be allocated to another order.</li>
        <li>A new part needs a line on the order first.</li>
      </ul>
      {overrun.length > 0 && (
        <div className="mt-1 text-warning-emphasis">
          {overrun.map((o, i) => <div key={i}><i className="bi bi-exclamation-triangle me-1"></i>{o.message || `Line ${o.so_line_id}: overrun`}</div>)}
        </div>
      )}
    </div>
  );
}
